import {navigateTo, useRouter} from '@typed-router';
import {storeToRefs} from 'pinia';
import {type RouteLocationNormalizedLoaded} from 'vue-router';
import {useLogger, useRoute} from '#imports';
import {safelyNavigateTo} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
import {useFreelancerSignupNavigationStore} from '~/features/freelancer-signup/navigation/store/FreelancerSignupNavigationStore';
import {FreelancerSignupStepName, FreelancerSignupStepRoute} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';

export function useFreelancerSignupNavigation() {
    const freelancerSignupNavigationStore = useFreelancerSignupNavigationStore();
    const {funnelContext, currentStep} = storeToRefs(freelancerSignupNavigationStore);

    const logger = useLogger();
    const route = useRoute();
    const router = useRouter();

    // Synchronize currentStep with step eventually updated via browser navigation
    router.afterEach((to: RouteLocationNormalizedLoaded) => {
        if (to.meta.funnelStepName) {
            freelancerSignupNavigationStore.updateCurrentStep(to.meta.funnelStepName as FreelancerSignupStepName);
        }
    });

    async function redirectOnWorkflowEnd(): Promise<void> {
        switch (funnelContext.value) {
            case FreelancerSignupContext.STRATEGY:
                await safelyNavigateTo('/profile/freelancer-signup/waiting-room/strategy', {externalToThisApp: true});
                return;
            case FreelancerSignupContext.OPEN:
                await safelyNavigateTo('/messages?waitSse=true', {externalToThisApp: true});
                return;
            case FreelancerSignupContext.REGULAR:
            default:
                await safelyNavigateTo('/profile');
        }
    }

    async function goToFirstStepToComplete(): Promise<void> {
        freelancerSignupNavigationStore.setCurrentStepAsFirstStepToComplete(_getContext());
        await navigateTo(currentStep.value.routeTo);
    }

    async function goToSigninWithRedirectionToFunnel(): Promise<void> {
        await safelyNavigateTo('/signin?redirect=/profile/freelancer-signup/welcome-to-malt', {externalToThisApp: true});
    }

    async function goToWelcomeBack(): Promise<void> {
        await navigateTo(FreelancerSignupStepRoute.WELCOME_BACK);
    }

    async function goToRegularWorkflow(): Promise<void> {
        await navigateTo(FreelancerSignupStepRoute.REDIRECT_TO_REGULAR_WORKFLOW);
    }

    async function initializeUsingWorkflow(): Promise<void> {
        const context = _getContext();
        freelancerSignupNavigationStore.updateCurrentContext(context);
        const currentStepNameFromMeta = _getMaybeFunnelStepNameFromRouteMeta(route);

        if (_shouldGoToWelcomeBack(currentStepNameFromMeta)) {
            return;
        }

        freelancerSignupNavigationStore.setCurrentStepAsFirstStepToComplete(context);

        if (currentStepNameFromMeta !== currentStep.value.name) {
            await safelyNavigateTo({path: currentStep.value.routeTo, query: route.query});
        }
    }

    async function next() {
        const nextStep = freelancerSignupNavigationStore.getMaybeNextStepConfiguration();
        if (nextStep != null) {
            await navigateTo(nextStep.routeTo);
        }
    }

    async function previous() {
        const previousStep = freelancerSignupNavigationStore.getMaybePreviousStepConfiguration();
        if (previousStep != null) {
            await navigateTo(previousStep.routeTo);
        }
    }

    return {
        redirectOnWorkflowEnd,
        goToFirstStepToComplete,
        goToRegularWorkflow,
        goToSigninWithRedirectionToFunnel,
        goToWelcomeBack,
        initializeUsingWorkflow,
        next,
        previous,
    };

    function _shouldGoToWelcomeBack(currentStepNameFromMeta: FreelancerSignupStepName | undefined): boolean {
        if (currentStepNameFromMeta !== FreelancerSignupStepName.WELCOME_BACK) {
            return false;
        }

        const email = route.query.email || freelancerSignupNavigationStore.getCurrentWorkflowEmail();

        return !!email;
    }

    function _getContext(): FreelancerSignupContext {
        return (route.meta?.funnelContext as FreelancerSignupContext) || FreelancerSignupContext.REGULAR;
    }

    function _getMaybeFunnelStepNameFromRouteMeta(currentRoute: RouteLocationNormalizedLoaded): FreelancerSignupStepName | undefined {
        if (import.meta.dev && currentRoute.path !== '/profile/freelancer-signup' && !currentRoute.meta?.funnelStepName) {
            logger.error(
                '⚠️ Missing "funnelStepName" in route meta ⚠️\r\nPlease use definePageMeta and define a meta "funnelStep" for the following page ' +
                    currentRoute.path,
            );

            if (!import.meta.server) {
                pushVJoySnackbar({
                    props: {
                        message: 'Missing "funnelStepName"<br /><b>Check your console</b>',
                        level: 'error',
                    },
                });
            }
        }

        return currentRoute.meta?.funnelStepName as FreelancerSignupStepName;
    }
}
